
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

// Firebase
import { db } from '../../firebase';
import {
  doc, collection, getDoc, setDoc, updateDoc,
  Timestamp, query, where, limit, getDocs, increment, arrayUnion
} from 'firebase/firestore';

// 日付操作
import { format } from 'date-fns';

// ユーティリティ
import { getAuth } from 'firebase/auth';
import toast from 'react-hot-toast';
import CloudinaryUploadWidget from '../common/CloudinaryUploadWidget';
import { checkNewBadges, getSubjectKey } from '../../utils/badgeLogic';

// --- react-iconsからアイコンをインポート ---
import { useRecordsUpdaterContext } from '../../contexts/RecordsUpdaterContext';
import {
  HiOutlineBookOpen,
  HiOutlineClock,
  HiOutlinePencil,
  HiOutlineCalendar,
  HiOutlineX,
  HiOutlinePhotograph,
  HiOutlineClipboardList
} from 'react-icons/hi';

// New Component
import PlanListInput from './PlanListInput';

const mainSubjects = ['国語', '数学', '理科', '社会', '英語'];

const MemoizedUploader = React.memo(({ onUpload, saving, imagePreview }) => {
    return (
        <div>
            <CloudinaryUploadWidget onUpload={onUpload} croppingAspectRatio={null} uploadType="study_note">
                <button
                    type="button"
                    disabled={saving}
                    className="w-full flex items-center justify-center mt-1 px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                    <HiOutlinePhotograph className="h-5 w-5 mr-2 text-gray-400 dark:text-gray-400" />
                    {imagePreview ? '画像を差し替える' : '画像を追加 (トリミング可)'}
                </button>
            </CloudinaryUploadWidget>
        </div>
    );
});


const RecordForm = ({ recordToEdit, onClose, isModalMode = false, initialData = null, linkedPlanId = null, onRecordComplete }) => {
  const { addRecord } = useRecordsUpdaterContext();
  const location = useLocation();
  
  // 'record' or 'daily_plan'
  const [postType, setPostType] = useState('record');
  
  // Common fields
  const [recordDate, setRecordDate] = useState('');
  const [recordTime, setRecordTime] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Record specific fields
  const [title, setTitle] = useState('');
  const [selectedSubjectType, setSelectedSubjectType] = useState('');
  const [customSubjectTitle, setCustomSubjectTitle] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [content, setContent] = useState('');
  const [imagePreview, setImagePreview] = useState('');

  // Daily Plan specific fields
  const [planItems, setPlanItems] = useState([
    { id: Date.now().toString(), subject: '数学', content: '', isCompleted: false }
  ]);

  const auth = getAuth();

  const formatDateForInput = (date) => {
    const pad = (num) => num.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`
    };
  };

  useEffect(() => {
    const initializeForm = (data, isEditing) => {
      setIsEditMode(isEditing);
      
      // Determine Post Type
      if (data?.type === 'daily_plan') {
        setPostType('daily_plan');
        if (data.planItems && Array.isArray(data.planItems)) {
            setPlanItems(data.planItems);
        }
      } else {
        setPostType('record');
        setPlanItems([{ id: Date.now().toString(), subject: '数学', content: '', isCompleted: false }]);
      }
      
      // If we are converting a plan (e.g. from study_plan type legacy or daily_plan), force 'record'
      if (linkedPlanId) {
          setPostType('record');
      }

      // Populate Record Fields
      const formTitle = data?.title || '';
      if (mainSubjects.includes(formTitle)) {
        setSelectedSubjectType(formTitle);
        setTitle(formTitle);
        setCustomSubjectTitle('');
      } else {
        setSelectedSubjectType(formTitle ? 'その他' : '');
        setCustomSubjectTitle(formTitle);
        setTitle(formTitle);
      }

      const totalMins = data?.totalMinutes || data?.minutes || 0;
      if (totalMins > 0) {
          setHours(String(Math.floor(totalMins / 60)));
          setMinutes(String(totalMins % 60));
      } else {
          setHours('');
          setMinutes('');
      }
      
      setContent(data?.content || '');
      setImagePreview(data?.imageUrl || '');

      // Date Logic
      let dateToFormat;
      if (data?.timestamp) {
        if (typeof data.timestamp.toDate === 'function') {
          dateToFormat = data.timestamp.toDate();
        } else if (typeof data.timestamp.getTime === 'function') {
          dateToFormat = data.timestamp;
        } else {
          try {
            dateToFormat = new Date(data.timestamp);
          } catch (e) {
            dateToFormat = new Date();
          }
        }
      } else {
        dateToFormat = new Date();
      }

      if (dateToFormat && !isNaN(dateToFormat.getTime())) {
        const { date, time } = formatDateForInput(dateToFormat);
        setRecordDate(date);
        setRecordTime(time);
      } else {
        const { date, time } = formatDateForInput(new Date());
        setRecordDate(date);
        setRecordTime(time);
      }
    };

    if (recordToEdit) {
      initializeForm(recordToEdit, true);
    } else {
      initializeForm(initialData, false);
    }
  }, [recordToEdit, initialData, linkedPlanId]);

  const handleUploadSuccess = useCallback((url) => {
    setImagePreview(url);
  }, []);

  const handleRecordSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (saving) return;
    setError('');

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError('ログインが必要です。');
      return;
    }
    
    // Validation
    if (postType === 'record') {
        if (!title.trim()) {
            setError('タイトルを入力してください。');
            return;
        }
    } else if (postType === 'daily_plan') {
        const validItems = planItems.filter(i => i.content.trim() !== '');
        if (validItems.length === 0) {
            setError('少なくとも1つのリスト項目を入力してください。');
            return;
        }
    }

    if (!recordDate || !recordTime) {
      setError('日時を入力してください。');
      return;
    }

    let recordTotalMinutes = 0;
    
    // Only validate and calc time if it's a Record
    if (postType === 'record') {
        const h = Number(hours) || 0;
        const m = Number(minutes) || 0;

        if (h < 0 || m < 0 || m >= 60) {
            setError('時間の入力が不正です。');
            toast.error('時間や分にマイナスの値は設定できません。');
            return;
        }
        recordTotalMinutes = h * 60 + m;
    }

    setSaving(true);

    const submissionDate = new Date(`${recordDate}T${recordTime}`);

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) throw new Error('ユーザー情報が存在しません。');
      const userData = userDoc.data();
      
      const visibility = userData.visibility || 'public';
      const isPublic = visibility === 'public';
      const allowedViewers = userData.allowedViewers || [];
      const currentTotalTime = userData.totalStudyTime || 0;

      let newRecord;
      let recordData = {
          timestamp: Timestamp.fromDate(submissionDate),
          userId: currentUser.uid,
          name: userData.name || '匿名ユーザー',
          userPhotoURL: userData.photoURL || '',
          class: userData.class || '',
          notificationSent: false,
          visibility: visibility,
          isPublic: isPublic,
          allowedViewers: allowedViewers,
      };

      if (postType === 'record') {
          recordData = {
              ...recordData,
              title: title.trim(),
              totalMinutes: recordTotalMinutes,
              content: content.trim(),
              imageUrl: imagePreview,
              type: null // Standard record
          };
      } else {
          // Daily Plan Data
          const validItems = planItems.filter(i => i.content.trim() !== '');
          recordData = {
              ...recordData,
              type: 'daily_plan',
              planItems: validItems,
              totalItems: validItems.length,
              completedCount: 0,
              status: 'active',
              totalMinutes: 0, // Plans start with 0 time
              title: '今日のクエスト' // Default title for display fallback
          };
      }

      if (isEditMode && recordToEdit) {
        // --- Edit Mode ---
        const recordRef = doc(db, 'records', recordToEdit.id);
        
        // Don't overwrite existing progress if editing a plan
        if (postType === 'daily_plan' && recordToEdit.planItems) {
             // We need to merge existing completion status if IDs match
             const mergedItems = recordData.planItems.map(newItem => {
                 const oldItem = recordToEdit.planItems.find(o => o.id === newItem.id);
                 return oldItem ? { ...newItem, isCompleted: oldItem.isCompleted } : newItem;
             });
             recordData.planItems = mergedItems;
             recordData.completedCount = mergedItems.filter(i => i.isCompleted).length;
        }

        await updateDoc(recordRef, recordData);
        newRecord = { id: recordToEdit.id, ...recordToEdit, ...recordData };

      } else {
        // --- Create Mode ---
        const recordRef = doc(collection(db, 'records'));
        await setDoc(recordRef, recordData);
        newRecord = { id: recordRef.id, ...recordData };

        // If this record completes a previous plan (simple conversion)
        if (linkedPlanId) {
            const planRef = doc(db, 'records', linkedPlanId);
            await updateDoc(planRef, { status: 'completed' });
        }
      }

      // --- Badge / Stats Logic Check (Only on Create Record) ---
      let unlockedBadges = [];
      if (!isEditMode && postType === 'record' && recordTotalMinutes > 0) {
          // 新しいバッジシステムのロジック
          // 1. badgeStatsの更新
          const subjectKey = getSubjectKey(recordData.title);
          
          await updateDoc(userRef, {
              [`badgeStats.${subjectKey}`]: increment(recordTotalMinutes),
              [`badgeStats.total_time`]: increment(recordTotalMinutes)
          });

          // 2. 獲得バッジの判定（更新後の値を想定して渡す必要があるが、
          // checkNewBadges内で加算して判定するように実装済み）
          unlockedBadges = checkNewBadges(userData, recordData, recordTotalMinutes);
          
          // 固定バッジの新規獲得があれば保存
          const staticBadges = unlockedBadges.filter(b => b.type === 'static').map(b => b.badge.id);
          if (staticBadges.length > 0) {
              await updateDoc(userRef, {
                  acquiredBadges: arrayUnion(...staticBadges)
              });
          }
      }

      // --- Team Score Update (Robust Create & Edit) ---
      // Update team scores if the user belongs to a team
      if (userData.currentTeam) {
          const updateBattleScore = async (dateObj, minutesDelta) => {
              if (minutesDelta === 0) return;
              
              const year = dateObj.getFullYear();
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getDate()).padStart(2, '0');
              const dateStr = `${year}-${month}-${day}`;

              try {
                  const battlesRef = collection(db, 'battles');
                  const q = query(
                      battlesRef,
                      where('startDate', '<=', dateStr),
                      where('endDate', '>=', dateStr),
                      limit(1)
                  );
                  const battleSnap = await getDocs(q);
                  
                  if (!battleSnap.empty) {
                      const battleDoc = battleSnap.docs[0];
                      const battleData = battleDoc.data();
                      
                      if (battleData.teams && battleData.teams.hasOwnProperty(userData.currentTeam)) {
                          const battleDocRef = doc(db, 'battles', battleDoc.id);
                          const teamField = `teams.${userData.currentTeam}`;
                          await updateDoc(battleDocRef, {
                              [teamField]: increment(minutesDelta)
                          });
                      }
                  }
              } catch (err) {
                  console.error("Failed to update battle score:", err);
              }
          };

          if (isEditMode && recordToEdit) {
              // Handle Edit: Revert old value, Add new value
              const oldMinutes = recordToEdit.totalMinutes || 0;
              let oldDate = null;
              
              if (recordToEdit.timestamp) {
                  oldDate = recordToEdit.timestamp.toDate ? recordToEdit.timestamp.toDate() : new Date(recordToEdit.timestamp);
              }

              if (oldDate && oldMinutes > 0) {
                  await updateBattleScore(oldDate, -oldMinutes);
              }

              if (postType === 'record' && recordTotalMinutes > 0) {
                  await updateBattleScore(submissionDate, recordTotalMinutes);
              }

          } else {
              // Handle Create
              if (postType === 'record' && recordTotalMinutes > 0) {
                  await updateBattleScore(submissionDate, recordTotalMinutes);
              }
          }
      }

      if (addRecord) {
        addRecord(newRecord);
      }

      const successMsg = postType === 'daily_plan' ? 'クエストを宣言しました！' : '記録を保存しました！';
      toast.success(isEditMode ? '更新しました！' : successMsg);
      
      // Trigger Level Up / Badge Animation Callback
      if (!isEditMode && postType === 'record' && recordTotalMinutes > 0 && onRecordComplete) {
          onRecordComplete({
              addedMinutes: recordTotalMinutes,
              oldTotal: currentTotalTime,
              newBadges: unlockedBadges // 進化型含む全バッジ情報
          });
      } else if (onClose) {
        onClose();
      }
      
      if (!isEditMode) {
        // Reset form
        setTitle('');
        setSelectedSubjectType('');
        setCustomSubjectTitle('');
        setHours('');
        setMinutes('');
        setContent('');
        setImagePreview('');
        setPlanItems([{ id: Date.now().toString(), subject: '数学', content: '', isCompleted: false }]);
        setError('');
        const { date, time } = formatDateForInput(new Date());
        setRecordDate(date);
        setRecordTime(time);
        setPostType('record'); // Reset to record
      }

    } catch (err) {
      console.error('Save failed:', err);
      setError(`保存に失敗しました: ${err.message}`);
      toast.error(`保存に失敗しました: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [auth, title, hours, minutes, content, recordDate, recordTime, imagePreview, isEditMode, recordToEdit, onClose, customSubjectTitle, addRecord, postType, linkedPlanId, planItems, onRecordComplete]);

  return (
    <div className={isModalMode ? "" : "pt-8 pb-16 bg-background-light dark:bg-background-dark"}>
      
      <form onSubmit={handleRecordSubmit} className={isModalMode ? "space-y-4 p-4" : "space-y-4 mt-6 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-md max-w-md mx-auto"}>
          
          {/* Mode Switcher */}
          {!linkedPlanId && !isEditMode && (
              <div className="flex bg-gray-100 dark:bg-gray-700 p-1 rounded-lg mb-4">
                  <button
                      type="button"
                      onClick={() => setPostType('record')}
                      className={`flex-1 flex items-center justify-center py-2 text-sm font-bold rounded-md transition-all ${
                          postType === 'record' 
                          ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm' 
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                  >
                      <HiOutlinePencil className="mr-1.5 h-4 w-4" />
                      記録する
                  </button>
                  <button
                      type="button"
                      onClick={() => setPostType('daily_plan')}
                      className={`flex-1 flex items-center justify-center py-2 text-sm font-bold rounded-md transition-all ${
                          postType === 'daily_plan' 
                          ? 'bg-white dark:bg-gray-600 text-orange-500 dark:text-orange-400 shadow-sm' 
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                  >
                      <HiOutlineClipboardList className="mr-1.5 h-4 w-4" />
                      宣言する
                  </button>
              </div>
          )}

          {/* Header Title if needed */}
          {linkedPlanId && (
              <div className="text-center mb-2">
                  <span className="inline-block bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded-full font-bold">宣言を達成</span>
              </div>
          )}

          {/* === Record Mode Fields === */}
          {postType === 'record' && (
            <>
              {/* Subject Selection */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <HiOutlineBookOpen className="h-5 w-5 text-gray-400 dark:text-gray-400" />
                </div>
                <select
                  id="subject-type"
                  name="subject-type"
                  value={selectedSubjectType ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedSubjectType(value);
                    if (value !== 'その他') {
                      setTitle(value);
                    } else {
                      setTitle(customSubjectTitle);
                    }
                  }}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  required={postType === 'record'}
                  disabled={saving}
                >
                  <option value="" disabled>教科を選択</option>
                  {mainSubjects.map(subject => (<option key={subject} value={subject}>{subject}</option>))}
                  <option value="その他">その他</option>
                </select>
              </div>
              {selectedSubjectType === 'その他' && (
                <div className="relative mt-4">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <HiOutlinePencil className="h-5 w-5 text-gray-400 dark:text-gray-400" />
                  </div>
                  <input
                    id="custom-subject"
                    name="custom-subject"
                    type="text"
                    value={customSubjectTitle ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomSubjectTitle(value);
                      setTitle(value);
                    }}
                    placeholder="教科名を入力"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
                    required={postType === 'record'}
                    disabled={saving}
                  />
                </div>
              )}

              {/* Hours & Minutes */}
              <div className="relative animate-fadeIn">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><HiOutlineClock className="h-5 w-5 text-gray-400 dark:text-gray-400" /></div>
                <div className="flex items-center space-x-2 pl-10">
                  <input
                    id="study-hours"
                    name="study-hours"
                    type="number"
                    value={hours ?? ''}
                    onChange={e => {
                      setHours(e.target.value);
                    }}
                    placeholder="時間"
                    min="0"
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
                    disabled={saving}
                  />
                  <input
                    id="study-minutes"
                    name="study-minutes"
                    type="number"
                    value={minutes ?? ''}
                    onChange={e => {
                      setMinutes(e.target.value);
                    }}
                    placeholder="分"
                    min="0"
                    max="59"
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="relative">
                <div className="absolute top-3 left-0 pl-3 flex items-center pointer-events-none"><HiOutlinePencil className="h-5 w-5 text-gray-400 dark:text-gray-400" /></div>
                <textarea
                  id="study-content"
                  name="study-content"
                  value={content ?? ''}
                  onChange={(e) => {
                    setContent(e.target.value);
                  }}
                  rows="4"
                  placeholder="勉強した内容や感想など"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
                  disabled={saving}
                />
              </div>

              <MemoizedUploader
                onUpload={handleUploadSuccess}
                saving={saving}
                imagePreview={imagePreview}
              />

              {imagePreview && (
                <div className="mt-4 relative">
                  <img src={imagePreview} alt="プレビュー" className="w-full h-auto rounded-lg" />
                  <button
                    type="button"
                    onClick={() => setImagePreview('')}
                    className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-1.5 hover:bg-opacity-75 transition-opacity"
                  >
                    <HiOutlineX className="h-5 w-5" />
                  </button>
                </div>
              )}
            </>
          )}

          {/* === Daily Plan Mode Fields === */}
          {postType === 'daily_plan' && (
              <PlanListInput 
                  items={planItems} 
                  setItems={setPlanItems} 
                  subjects={mainSubjects} 
              />
          )}

          {/* Common Date & Time (Bottom) */}
          <div className="relative border-t dark:border-gray-700 pt-4 mt-2">
            <div className="flex items-center space-x-2">
              <div className="flex items-center pointer-events-none">
                  <HiOutlineCalendar className="h-5 w-5 text-gray-400 dark:text-gray-400" />
              </div>
              <input
                id="record-date"
                name="record-date"
                type="date"
                value={recordDate ?? ''}
                onChange={(e) => setRecordDate(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 disabled:bg-gray-100 dark:disabled:bg-gray-700"
                required
                disabled={saving || isEditMode}
              />
              <input
                id="record-time"
                name="record-time"
                type="time"
                value={recordTime ?? ''}
                onChange={(e) => setRecordTime(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                required
                disabled={saving}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            {isEditMode && (
                <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50">
                    キャンセル
                </button>
            )}
            <button 
                type="submit" 
                disabled={saving} 
                className={`w-full flex-1 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                    ${postType === 'daily_plan' 
                        ? 'bg-orange-500 hover:bg-orange-600 focus:ring-orange-500' 
                        : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                    }`}
            >
              {saving ? '保存中...' : (isEditMode ? '更新する' : (postType === 'daily_plan' ? 'クエストを宣言' : '記録を保存'))}
            </button>
          </div>

          {error && <p className="text-red-500 dark:text-red-400 text-center text-sm mt-2">{error}</p>}
        </form>
      </div>
  );
};

export default React.memo(RecordForm);
